import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomChartReport } from 'src/app/interfaces/custom-chart-report/custom-chart-report.interface';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { InvoicingReportFilterModel } from 'src/app/models/invoicing/invoicing-report-filter.model';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoicingReportService } from 'src/app/services/invoicing/invoicing-report.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { RentalPeriodService } from 'src/app/services/rental/rental-period.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-invoicing-concept',
  templateUrl: './invoicing-concept.component.html',
  styleUrls: ['./invoicing-concept.component.scss'],
  standalone: false
})
export class InvoicingConceptComponent implements OnInit {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  filteredEnterprise: any[] = [];
  formInvoicingReportFilter: FormGroup = new FormGroup({});
  listBranch: any[] = [];
  listEnterprise: any[] = [];
  nameWindow: string = 'Informe de Facturación por Concepto';
  projectName = ProjectName.projectName;
  customChartReport: CustomChartReport;

  constructor(private branchService: BranchService,
    private dateConverterService: DateConverterService,
    private enterpriseService: EnterpriseService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private invoicingReportService: InvoicingReportService,
    private rentalPeriodService: RentalPeriodService,
    private title: Title,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getAllLists();
    this.getFirstBranchByUserAuthenticatedAsync();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeBranch(branchId: number | null) {
    this.filterEnterpriseByBranchId(branchId);
    if (branchId != 0 && branchId != null) {
      this.getFirstEnterpriseByBranch(branchId);
    } else {
      this.formInvoicingReportFilter.controls["enterpriseId"].patchValue(0);
      this.formInvoicingReportFilter.controls["branchId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeEnterprise(enterpriseId: number | null) {
    if (enterpriseId != 0 && enterpriseId != null) {
      const branchId = this.listEnterprise.find(x => x.value === enterpriseId)!.branchId;
      this.formInvoicingReportFilter.controls["branchId"].patchValue(branchId);
    } else {
      this.formInvoicingReportFilter.controls["enterpriseId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  clear() {
    this.changeFilter();
    this.validateForm();
    this.getFirstBranchByUserAuthenticatedAsync();
  }

  async exportExcel() {
    if (this.changeFilters) {
      const modelRentalReportFilter = this.formInvoicingReportFilter.value as InvoicingReportFilterModel;
      try {
        const response = await firstValueFrom(this.invoicingReportService.exportInvoicingReportInvoicingConcept(modelRentalReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Facturación por Concepto.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  filterEnterpriseByBranchId(branchId: number | null) {
    if (branchId != null) {
      this.filteredEnterprise = this.listEnterprise.filter(x => x.branchId === branchId);
      this.formInvoicingReportFilter.controls["enterpriseId"].patchValue(0);
    } else {
      this.filteredEnterprise = this.listEnterprise;
    }
  }

  async getAllLists() {
    try {
      const [branches, enterprises] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.enterpriseService.getAllEnterprisesForSelectAsync()),
      ]);
      this.listBranch = branches;
      this.listEnterprise = enterprises;
      this.filteredEnterprise = this.listEnterprise;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.formInvoicingReportFilter.controls["branchId"].patchValue(branch.branchId);
      this.getLastRentalPeriod(branch.branchId);
      this.filterEnterpriseByBranchId(branch.branchId)
      this.getFirstEnterpriseByBranch(branch.branchId);
    } catch (error) {
      console.error("Error al consultar la sucursal:", error);
    }
  }

  async getFirstEnterpriseByBranch(branchId: number) {
    try {
      const response = await firstValueFrom(this.enterpriseService.getFirstEnterpriseByBranch(branchId));
      if (!response) {
        this.toastHandlerService.showToast('error', 'La Sucursal no tiene empresas creadas.');
      } else {
        this.formInvoicingReportFilter.controls["enterpriseId"].patchValue(response.enterpriseId);
      }
    } catch (error) {
      console.error('Error al obtener la primera empresa por sucursal.', error);
    }
  }


  async getLastRentalPeriod(branchId: number) {
    try {
      const rentalPeriod = await firstValueFrom(this.rentalPeriodService.getLastRentalPeriod(branchId));
      this.formInvoicingReportFilter.controls["initialDate"].patchValue(this.dateConverterService.convertDatePreserveDay(rentalPeriod.initialDate));
      this.formInvoicingReportFilter.controls["finalDate"].patchValue(this.dateConverterService.convertDatePreserveDay(rentalPeriod.finalDate));
    } catch (error) {
      console.error("Error al consultar el periodo de alquiler:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    Object.keys(this.formInvoicingReportFilter.controls).forEach(key => {
      const control = this.formInvoicingReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formInvoicingReportFilter.valid) {
      const modelRentalReportFilter = this.formInvoicingReportFilter.value as InvoicingReportFilterModel;

      if (modelRentalReportFilter.finalDate < modelRentalReportFilter.initialDate) {
        Swal.fire({
          icon: 'warning',
          title: 'Atención.',
          text: 'La fecha final no puede ser menor al fecha inicial.'
        });
        return;
      }

      try {
        this.dataReport = await firstValueFrom(this.invoicingReportService.getInvoicingConcept(modelRentalReportFilter));
        if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
          this.changeFilters = true;

          //Capturo los datos para la grafica
          const data: any = [];
          this.dataReport.data.forEach(row => {
            data.push(
              {
                name: row.conceptInvoicingCode,
                value: row.totalvalue
              }
            );
          });
          const result = data;
          this.customChartReport = {
            result: result,
            legendTitle: 'Conceptos',
            showXAxisLabel: true,
            xAxisLabel: 'Concepto',
            showYAxisLabel: true,
            yAxisLabel: 'Vr. Facturado'
          };
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      initialDate: {
        required: 'El campo Desde es obligatorio.'
      },
      finalDate: {
        required: 'El campo Hasta es obligatorio.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInvoicingReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formInvoicingReportFilter = this.formBuilder.group({
      branchId: [0],
      enterpriseId: [0],
      initialDate: ['', [Validators.required]],
      finalDate: ['', [Validators.required]]
    });
  }
}
